/* Megamenu Link List — vanilla port of
 * orion-v3.0/src/js/includes/megamenu.js.
 *
 * Each .megamenu-list-js block is a 3-tier hover/focus navigation
 * (main → sub → sub-sub). The Liquid template renders all three
 * tiers in a shared 1-row grid so only the panel matching the
 * currently active link is visible at a time (via .is-active +
 * CSS opacity transitions).
 *
 * Data attributes consumed (set by blocks/_megamenu-linklist.liquid):
 *   data-default-main      Handle of the first top-level menu item;
 *                           used as the "preselect" target when the
 *                           merchant has show_sublist on.
 *   data-show-sublist      'true' | 'false' — gates the initial
 *                           activation pass.
 *   data-mega-main         A top-level menu link.
 *   data-mega-panel        Container holding one main link's children.
 *   data-nested            A sub-link.
 *   data-has-grandchildren On a sub-link that has its own children.
 *   data-mega-sub          Container holding one sub-link's children.
 *
 * Initialisation is idempotent — calling init() on an already-bound
 * .megamenu-list-js is a no-op (guarded by `data-initialized`). */

class MegamenuLinkList {
  constructor(root) {
    this.root = root;
    this.mainLinks = Array.from(root.querySelectorAll('[data-mega-main]'));
    this.panels = Array.from(root.querySelectorAll('[data-mega-panel]'));
    this.subLinks = Array.from(root.querySelectorAll('[data-nested]'));
    this.subPanels = Array.from(root.querySelectorAll('[data-mega-sub]'));
    this.defaultMain = root.dataset.defaultMain;
    this.showSublist = root.dataset.showSublist === 'true';

    const megamenuEl = root.closest('.navbar-megamenu__container') || root.closest('.navbar-megamenu');
    this.productPairs = megamenuEl
      ? Array.from(megamenuEl.querySelectorAll('[data-product-pair-handle]'))
      : [];
  }

  init() {
    if (this.root.dataset.initialized === 'true') return;

    // Every main link switches active state on hover/focus — including
    // links with no child panel. activateMain() handles the no-panel
    // case: it clears every panel + sub-panel and (via its hasPanel
    // guard) leaves the hovered link without a sticky .is-active, so
    // a previously hovered link's child column can't linger and read
    // as if it belongs to the childless link.
    this.mainLinks.forEach((link) => {
      const activate = () => this.activateMain(link.dataset.megaMain);
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    // Sub-links mirror the main tier: links with grandchildren open
    // their sub-sub panel; leaves clear column 3 so a previous
    // sub-link's grandchildren can't linger next to them. Leaves
    // keep their natural :hover only (deactivateSubPanels never
    // paints .is-active).
    this.subLinks.forEach((link) => {
      const activate = link.dataset.hasGrandchildren === 'true'
        ? () => this.activateSubPanel(link.dataset.nested)
        : () => this.deactivateSubPanels();
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    // Preselect the cached main link so the deepest panel is visible
    // straight away when the megamenu opens (only when show_sublist
    // is on — otherwise there's nothing to preselect).
    const initialMain = this.defaultMain
      || (this.mainLinks[0] && this.mainLinks[0].dataset.megaMain);
    if (initialMain && this.showSublist) {
      this.activateMain(initialMain);
    }

    // Listen for the enclosing megamenu's close event so we can
    // wipe link / panel state and return to the initial pass.
    // megamenu.js dispatches `megamenu:closed` whenever a megamenu
    // loses its .is-active class (closeAll + cross-trigger switch).
    const megamenuEl = this.root.closest('[data-megamenu]');
    if (megamenuEl) {
      megamenuEl.addEventListener('megamenu:closed', () => this.reset());
    }

    this.root.dataset.initialized = 'true';
  }

  reset() {
    // Wipe every active state in one pass.
    this.mainLinks.forEach((link) => link.classList.remove('is-active'));
    this.panels.forEach((panel) => panel.classList.remove('is-active'));
    this.deactivateSubPanels();

    // Re-apply the default-main preselect so the next open starts
    // identical to the first open (rather than blank when
    // showSublist is on).
    const initialMain = this.defaultMain
      || (this.mainLinks[0] && this.mainLinks[0].dataset.megaMain);
    if (initialMain && this.showSublist) {
      this.activateMain(initialMain);
    }
  }

  deactivateSubPanels() {
    this.subPanels.forEach((panel) => panel.classList.remove('is-active'));
    this.subLinks.forEach((link) => link.classList.remove('is-active'));
  }

  activateSubPanel(handle) {
    if (!handle) {
      this.deactivateSubPanels();
      return;
    }
    this.subPanels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.megaSub === handle);
    });
    this.subLinks.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.nested === handle);
    });
  }

  activateMain(handle) {
    if (!handle) return;

    // Only paint .is-active on the matching link when it actually
    // opens a panel. Childless links route through here on hover /
    // focus (and the default-main preselect can target one too) —
    // they get the full panel/sub-panel clear below but never a
    // sticky .is-active of their own.
    const hasPanel = this.panels.some(
      (p) => p.dataset.megaPanel === handle
    );

    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.megaPanel === handle);
    });
    this.mainLinks.forEach((link) => {
      link.classList.toggle(
        'is-active',
        hasPanel && link.dataset.megaMain === handle
      );
    });

    this.productPairs.forEach((pair) => {
      pair.classList.toggle('is-active', pair.dataset.productPairHandle === handle);
    });

    // Always clear any sub-panel + sub-link state when switching to
    // a different main. Column 3 only appears when the user
    // explicitly hovers / focuses a sub-link that has grandchildren.
    this.deactivateSubPanels();
  }
}

function start() {
  document.querySelectorAll('.megamenu-list-js').forEach((root) => {
    new MegamenuLinkList(root).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
